import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Polls fetcher() every intervalMs (wallboard: 15 s, ADR-0006). A failed poll keeps the last data
 * and sets stale, so views can show "updated n s ago" instead of going blank.
 * Polling pauses while the tab is hidden and refreshes when it becomes visible again.
 */
export function usePolling(fetcher, intervalMs, { enabled = true, deps = [] } = {}) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(enabled);
  const [lastUpdated, setLastUpdated] = useState(null);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const inFlight = useRef(false);
  const generation = useRef(0);

  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    const thisGeneration = generation.current;
    try {
      const result = await fetcherRef.current();
      if (thisGeneration !== generation.current) return;
      setData(result);
      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      if (thisGeneration !== generation.current) return;
      setError(err);
    } finally {
      inFlight.current = false;
      if (thisGeneration === generation.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    generation.current += 1;
    inFlight.current = false;
    setLoading(true);
    refresh();
    const timer = setInterval(() => {
      if (document.visibilityState !== 'hidden') refresh();
    }, intervalMs);
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, intervalMs, refresh, ...deps]);

  return { data, error, loading, lastUpdated, stale: Boolean(error && data), refresh };
}

export const DASHBOARD_REFRESH_MS = 5 * 60 * 1000;
export const RETURN_REFRESH_AFTER_MS = 60 * 1000;

/**
 * A counter for background dashboard refreshes: it steps intervalMs after the last step (or after resetKey changed)
 * while the tab is visible, and when the tab becomes visible again after at least minHiddenMs hidden or with a step
 * that fell due while hidden. It starts at 0, so callers skip the first render.
 */
export function useRefreshTick(resetKey, { intervalMs = DASHBOARD_REFRESH_MS, minHiddenMs = RETURN_REFRESH_AFTER_MS } = {}) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let hiddenAt = document.visibilityState === 'hidden' ? Date.now() : null;
    let due = false;
    const step = () => setTick((n) => n + 1);
    const timer = setTimeout(() => {
      if (document.visibilityState === 'hidden') due = true;
      else step();
    }, intervalMs);
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (hiddenAt == null) hiddenAt = Date.now();
        return;
      }
      const away = hiddenAt == null ? 0 : Date.now() - hiddenAt;
      hiddenAt = null;
      if (due || away >= minHiddenMs) step();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [tick, resetKey, intervalMs, minHiddenMs]);
  return tick;
}

/** Seconds since a Date, re-rendering every second ("updated n s ago"). */
export function useSecondsSince(date) {
  const [now, setNow] = useState(() => Date.now());
  const timestamp = date ? date.getTime() : null;
  useEffect(() => {
    if (timestamp == null) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [timestamp]);
  return timestamp == null ? null : Math.max(0, Math.round((now - timestamp) / 1000));
}

export default usePolling;
